import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select, {
  CSSObjectWithLabel,
  GroupBase,
  StylesConfig,
} from "react-select";
import axiosClient from '../../libs/axiosClient';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import TermsModal from '../../components/TermsModal/TermsModal';
import styles from './Join.module.scss';
import ukFlag from "../../assets/hospitalReservation/ukFlag.png";
import chinaFlag from "../../assets/hospitalReservation/chinaFlag.png";
import japanFlag from "../../assets/hospitalReservation/japanFlag.png";
import arabFlag from "../../assets/hospitalReservation/arabFlag.png";
import kakaoIcon from "../../assets/hospitalReservation/kakaotalkIcon.png";
import whatsappIcon from "../../assets/hospitalReservation/whatsappIcon.png";
import lineIcon from "../../assets/hospitalReservation/lineIcon.png";
import wechatIcon from "../../assets/hospitalReservation/wechatIcon.png";

const customStyles: StylesConfig<
  {
    value: string;
    label: JSX.Element;
  },
  false,
  GroupBase<{ value: string; label: JSX.Element }>
> = {
  control: (provided: CSSObjectWithLabel) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    height: "40px",
    padding: "5px",
    border: "1px solid #ccc",
    borderRadius: "5px",
    width: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
  }),
  valueContainer: (provided: CSSObjectWithLabel) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: "0 8px",
  }),
  placeholder: (provided: CSSObjectWithLabel) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "#aaa",
    fontSize: "14px",
  }),
  singleValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    gap: "8px",
    img: {
      width: "20px",
      height: "20px",
      objectFit: "cover",
      marginRight: "7px",
    },
  }),
  menu: (provided: CSSObjectWithLabel) => ({
    ...provided,
    zIndex: 5,
    padding: "5px 0",
  }),
  option: (provided: CSSObjectWithLabel, state) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    backgroundColor: state.isFocused ? "#f0f0f0" : "white",
    color: "#333",
    padding: "8px 12px",
    gap: "10px",
    fontSize: "14px",
    img: {
      width: "30px",
      height: "30px",
      objectFit: "cover",
      marginRight: "10px",
    },
  }),
  dropdownIndicator: (provided: CSSObjectWithLabel) => ({
    ...provided,
    padding: "5px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
};

function Join() {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState('consultation');
  const [isModalOpen, setModalOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState("");
  const [email, setEmail] = useState("");
  const [passportName, setPassportName] = useState<string>("");
  const [selectedMessenger, setSelectedMessenger] = useState<string | null>(
    null
  );
  const [selectedNationality, setSelectedNationality] = useState<string | null>(null);
  
  const handleSubmit = async () => {
    if (!username || !password || !firstName || !lastName || !dateOfBirth || !gender || !selectedNationality || !selectedMessenger) {
      alert("Please fill in all required fields!");
      return;
    }
    
    if (password !== confirmPassword) {
      alert("Password and confirm password do not match.");
      return;
    }

    const requestData = {
      uid: username,
      password,
      first_name: firstName,
      last_name: lastName,
      gender,
      email,
      messenger: selectedMessenger,
      country: selectedNationality,
    };

    try {
      const response = await axiosClient.post(
        "/api/v1/users",
        requestData
      );
      alert("User registration successful!");
      navigate("/login");
    } catch (error: any) {
      if (error.response?.status === 400) {
        const detail = error.response.data?.detail;
  
        if (detail === "User with this UID already exists.") {
          alert("The username is already taken. Please choose a different username.");
        } else if (detail === "User with this email already exists.") {
          alert("The email is already taken. Please use a different email address.");
        } else {
          alert("Failed to register user. Please try again.");
        }
      } else {
        alert("An unexpected error occurred. Please try again.");
      }
      console.error("Error:", error.response?.data || error.message);
    }
  };
  
  const nationalityOptions = [
    {
      value: "EN",
      label: (
        <div className={styles.option}>
          <img src={ukFlag} alt="uk Flag" className={styles.icon} />
          English
        </div>
      ),
    },
    {
      value: "JA",
      label: (
        <div className={styles.option}>
          <img src={japanFlag} alt="Japan Falg" className={styles.icon} />
          日本
        </div>
      ),
    },
    {
      value: "ZH",
      label: (
        <div className={styles.option}>
          <img src={chinaFlag} alt="China Flag" className={styles.icon} />
          中国
        </div>
      ),
    },
    {
      value: "AR",
      label: (
        <div className={styles.option}>
          <img src={arabFlag} alt="Arab Flag" className={styles.icon} />
          عربي
        </div>
      ),
    },
  ];
  
  const messengerOptions = [
    {
      value: "kakao",
      label: (
        <div className={styles.option}>
          <img src={kakaoIcon} alt="KakaoTalk" className={styles.icon} />
          KakaoTalk
        </div>
      ),
    },
    {
      value: "line",
      label: (
        <div className={styles.option}>
          <img src={lineIcon} alt="Line" className={styles.icon} />
          Line
        </div>
      ),
    },
    {
      value: "wechat",
      label: (
        <div className={styles.option}>
          <img src={wechatIcon} alt="WeChat" className={styles.icon} />
          WeChat
        </div>
      ),
    },
    {
      value: "whatsapp",
      label: (
        <div className={styles.option}>
          <img src={whatsappIcon} alt="WhatsApp" className={styles.icon} />
          WhatsApp
        </div>
      ),
    },
  ];

  const [modalContent, setModalContent] = useState({
    title: '',
    content: '',
  });

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
  };
  const { t, i18n } = useTranslation();

  const handleOpenModal = (title: string, content: string) => {
    setModalContent({ title, content });
    setModalOpen(true);
  };
  const handleCloseModal = () => setModalOpen(false);

  const [agreements, setAgreements] = useState({
    agreeAll: false,
    terms: false,
    privacyCollection: false,
    privacyDelegation: false,
    thirdPartyAgreement: false,
  });
  
  const handleDuplicateCheck = async (category: "uid" | "email", value: string) => {
    if (!value) {
      alert(
        t(
          category === "uid"
            ? "join.enterUsername"
            : "join.enterEmail"
        )
      );
      return;
    }
  
    try {
      const response = await axiosClient.get(
        `api/v1/users/duplicate/${category}`,
        {
          params: { value },
        }
      );
  
      if (response.status === 200) {
        alert(
          t(
            category === "uid"
              ? "join.usernameAvailable"
              : "join.emailAvailable"
          )
        );
      }
    } catch (error: any) {
      if (error.response?.status === 409) {
        const message = t(
          category === "uid"
            ? "join.usernameAlreadyExists"
            : "join.emailAlreadyExists"
        );
        alert(message);
      } else {
        alert(t("join.checkFailed"));
      }
      console.error(error);
    }
  };
  
  //약관 동의 관련
  const handleAgreeAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setAgreements({
      agreeAll: checked,
      terms: checked,
      privacyCollection: checked,
      privacyDelegation: checked,
      thirdPartyAgreement: checked,
    });
  };

  const handleIndividualChange = (key: keyof typeof agreements) => {
    setAgreements((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      const allChecked =
        updated.terms &&
        updated.privacyCollection &&
        updated.privacyDelegation &&
        updated.thirdPartyAgreement;
      return { ...updated, agreeAll: allChecked };
    });
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'en';

    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  return (
    <>
      <Header selectedTab={selectedTab} onTabChange={handleTabChange} />
      <div className={styles.container}>
        <h2 className={styles.title}>{t('join.title')}</h2>
        <div className={styles.form}>
          <div className={styles.formGroup}>
            <label>
              <span className={styles.requiredText}>{t('join.required')}</span> {t('join.id')}
            </label>
            <div className={styles.inputGroup}>
              <input type="text" value={username}
              onChange={(e) => setUsername(e.target.value)} placeholder={t('join.usernamePlaceholder')} />
              <button 
                className={styles.idCheckButton}
                onClick={() => handleDuplicateCheck("uid", username)}
              >
                {t('join.checkDuplicate')}
              </button>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>
              <span className={styles.requiredText}>{t('join.required')}</span> {t('join.password')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('join.passwordPlaceholder')}
            />
          </div>
          <div className={styles.formGroup}>
            <label>
              <span className={styles.requiredText}>{t('join.required')}</span> {t('join.confirmPassword')}
            </label>
            <input
              type="password"
              placeholder={t('join.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          <div className={styles.formGroup}>
            <label>
              <span className={styles.requiredText}>{t('join.required')}</span> {t('join.passportName')}
            </label>
            <div className={styles.inputRow}>
              <input
                type="text"
                value={firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                placeholder={t('join.firstName')}
              />
              <input
                type="text"
                value={lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                placeholder={t('join.lastName')}
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>
              <span className={styles.requiredText}>{t('join.required')}</span> {t('join.dateOfBirth')}
            </label>
            <input
              type="text"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              placeholder={t('join.dateOfBirthPlaceholder')}
            />
          </div>
          <div className={styles.formGroup}>
            <label>
              <span className={styles.requiredText}>{t('join.required')}</span> {t('join.gender')}
            </label>
            <div className={styles.genderOptions}>
              <button
                type="button"
                className={`${styles.genderButton} ${gender === 'F' ? styles.selected : ''}`}
                onClick={() => setGender('F')}
              >
                {t('join.female')}
              </button>
              <button
                type="button"
                className={`${styles.genderButton} ${gender === 'M' ? styles.selected : ''}`}
                onClick={() => setGender('M')}
              >
                {t('join.male')}
              </button>
              <button
                type="button"
                className={`${styles.genderButton} ${gender === 'others' ? styles.selected : ''}`}
                onClick={() => setGender('N')}
              >
                {t('join.others')}
              </button>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>
              {t('join.email')}
            </label>
            <div className={styles.inputGroup}>
              <input
                type="email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                placeholder={t('join.emailPlaceholder')}
              />
              <button 
                className={styles.emailCheckButton}
                onClick={() => handleDuplicateCheck("email", email)}
              >
                {t('join.checkDuplicate')}
              </button>
            </div>
          </div>
          <div className={styles.customFormGroup}>
            <label>
              <span className={styles.requiredText}>{t('join.required')}</span> {t('join.country')}
            </label>
            <Select
              options={nationalityOptions}
              styles={customStyles}
              placeholder={i18n.t("join.countryPlaceholder")}
              classNamePrefix="react-select"
              onChange={(option) =>
                setSelectedNationality(option?.value || null)
              }
            />
          </div>
          <div className={styles.formGroup}>
            <label>
              <span className={styles.requiredText}>{t('join.required')}</span> {t('join.phone')}
            </label>
            <input type="text" placeholder={t('join.phonePlaceholder')} />
          </div>
          <div className={styles.customFormGroup}>
            <label>
              <span className={styles.requiredText}>{t('join.required')}</span> {t('join.messenger')}
            </label>
            <Select
              options={messengerOptions}
              styles={customStyles}
              placeholder={i18n.t("join.messengerPlaceholder")}
              classNamePrefix="react-select"
              onChange={(option) =>
                setSelectedMessenger(option?.value || null)
              }
            />
          </div>
          <div className={styles.formGroup}>
            <label>
              <span className={styles.requiredText}>{t('join.required')}</span> {t('join.messengerId')}
            </label>
            <input type="text" placeholder={t('join.messengerIdPlaceholder')} />
          </div>
          
          <div className={styles.termsSection}>
            <h3 className={styles.termsTitle}>
              <span className={styles.requiredText}>{t('join.required')}</span> {t('join.termsOfService')}
            </h3>
          <div className={styles.termsBox}>
            <div
            dangerouslySetInnerHTML={{
              __html: `
                <style>
                  h3 {
                    margin-top: 15px;
                    margin-bottom: 10px;
                    font-size: 15px;
                  }
                  p {
                    margin-top: 5px;
                    margin-bottom: 5px;
                    font-size: 12px;
                  }
                </style>
                ${t('termsOfUse.content')}
              `,
            }}
          ></div>
          </div>
          <h3 className={styles.termsTitle}>
              <span className={styles.requiredText}>{t('join.required')}</span> {t('join.privacyPolicy')}
            </h3>
          <div className={styles.termsBox}>
            <div className={styles.termsContent}>
              <div
              dangerouslySetInnerHTML={{
                __html: `
                  <style>
                    h3 {
                      margin-top: 15px;
                      margin-bottom: 10px;
                      font-size: 15px;
                    }
                    p {
                      margin-top: 5px;
                      margin-bottom: 5px;
                      font-size: 12px;
                    }
                  </style>
                  ${t('privacyOfPolicy.content')}
                `,
              }}
            ></div>
            </div>
          </div>
          </div>
          <button className={styles.submitButton} onClick={() => handleSubmit()}>{t('join.submit')}</button>
        </div>
      </div>

      {isModalOpen && (
        <TermsModal
          title={modalContent.title}
          content={modalContent.content}
          onClose={handleCloseModal}
        />
      )}
      <Footer />
    </>
  );
}

export default Join;
